import React, { useEffect, useState } from "react";
import apiClient from "../../../services/apiClient";
import { getHeaders } from "../../ApiHeaders/apiHeaders";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";

interface AssetDropDownProps {
  selectedAsset: string;
  onChange: (assetId: string, assetName: string) => void;
  disabled: boolean;
  className?: string;
  setAvailableAssets?: (
    assets: { asset_id: number; asset_name: string }[]
  ) => void;
  onFocus?: () => void;
}

const AssetDropDown: React.FC<AssetDropDownProps> = ({
  selectedAsset,
  onChange,
  disabled,
  className = "",
  setAvailableAssets,
  onFocus,
}) => {
  const [assets, setAssets] = useState<
    { asset_id: number; asset_name: string }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssets = async () => {
      const headers = await getHeaders();
      try {
        setLoading(true);
        datadogRum.startView("AssetDropdown");

        const response = await apiClient.get(`/assets/?page=1&page_size=500`, {
          headers,
        });
        const fetchedAssets = response.data.response.data;

        const mappedAssets = fetchedAssets.map((asset: any) => ({
          asset_id: asset.asset_id,
          asset_name: asset.asset_name,
        }));

        setAssets(mappedAssets);
        if (setAvailableAssets) {
          setAvailableAssets(mappedAssets);
        }

        datadogRum.addAction("Fetched Assets Successfully", {
          count: fetchedAssets.length,
        });
      } catch (err) {
        console.error("Failed to fetch assets", err);
        setError("Failed to fetch assets");
        datadogRum.addError("Failed to fetch assets", { error: err });
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, [setAvailableAssets]);

  const options = assets.map((asset) => ({
    value: asset.asset_id.toString(),
    label: asset.asset_name,
  }));

  const handleAssetChange = (selectedValue: string) => {
    const selected = assets.find(
      (asset) => asset.asset_id.toString() === selectedValue
    );
    if (selected) {
      onChange(selected.asset_id.toString(), selected.asset_name);
      datadogRum.addAction("Asset Selected", {
        assetId: selected.asset_id,
        assetName: selected.asset_name,
      });
    }
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedAsset}
      onValueChange={handleAssetChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder="Select an asset"
      onFocus={onFocus}
    />
  );
};

export default AssetDropDown;
